import { GetServerSidePropsContext } from 'next';
import logger from './logger';
import { isClientSide } from './constants';

// This prefix can be used to force new cookies/local storage/session storage values to be used instead of the old ones
// Be carefull changing this because it affects ALL users in ALL environments!
export const STORAGE_NAME_PREFIX = '_likie_v1.0.7';

export const appCookies = {
  selectedLocationCookieName: '_selected-location',
  locationHasBeenAskedCookieName: '_location-has-been-asked',
  zustandAuthorizationStorageName: '_zustand_authorization',
  zustandCartStorageName: '_zustand_cart',
  zustandLocationStorageName: '_zustand_location',
  zustandDrugSearchHistoryStorageName: '_zustand_drug-search-history',
  zustandViewedDrugsStorageName: '_zustand_viewed_drugs',
  zustandSessionConfigStorageName: '_zustand_session_config',
  zustandUseOneDrugOrderStorage: '_zustand_one_drug_order',
  zustandProposalCartStorage: '_zustand_proposal_cart'
};

// Cookies
export const selectedLocationCookieName = `${STORAGE_NAME_PREFIX}${appCookies.selectedLocationCookieName}`;
export const locationHasBeenAskedCookieName = `${STORAGE_NAME_PREFIX}${appCookies.locationHasBeenAskedCookieName}`;

// Zustand storage cookie names
export const zustandAuthorizationStorageName = `${STORAGE_NAME_PREFIX}${appCookies.zustandAuthorizationStorageName}`;
export const zustandCartStorageName = `${STORAGE_NAME_PREFIX}${appCookies.zustandCartStorageName}`;
export const zustandLocationStorageName = `${STORAGE_NAME_PREFIX}${appCookies.zustandLocationStorageName}`;
export const zustandDrugSearchHistoryStorageName = `${STORAGE_NAME_PREFIX}${appCookies.zustandDrugSearchHistoryStorageName}`;
export const zustandViewedDrugsStorageName = `${STORAGE_NAME_PREFIX}${appCookies.zustandViewedDrugsStorageName}`;
export const zustandSessionConfigStorageName = `${STORAGE_NAME_PREFIX}${appCookies.zustandSessionConfigStorageName}`;
export const zustandUseOneDrugOrderStorage = `${STORAGE_NAME_PREFIX}${appCookies.zustandUseOneDrugOrderStorage}`;
export const zustandProposalCartStorage = `${STORAGE_NAME_PREFIX}${appCookies.zustandProposalCartStorage}`;

export const largeExpirationCookieOpts = {
  expires: 365
};

export const isJSONValueValid = (value: string) => {
  return value && value !== 'undefined' && value !== 'null';
};

export const safeJSONParse = (value: string) => {
  if (isJSONValueValid(value)) {
    return JSON.parse(value);
  }
  return null;
};

export const isAuthorizationCookieExistsOnServerSide = (
  context: GetServerSidePropsContext
): boolean => {
  const serverAuthZustandStore = safeJSONParse(
    context?.req?.cookies[zustandAuthorizationStorageName]
  );
  const token = serverAuthZustandStore?.state?.userToken;
  return !!token && token !== '';
};

export const generateCookieString = ({
  path,
  expires,
  ...fields
}: {
  path?: string;
  expires?: string | number | Date;
} & { [cookieName: string]: unknown }): string => {
  const cookieString = Object.keys(fields).map(
    (name) => `${name}=${fields[name]};`
  );
  if (path) {
    cookieString.push(`Path=${path};`);
  }
  if (expires) {
    let expiresString = expires;
    if (typeof expires === 'number') {
      expiresString = new Date(Date.now() + expires * 864e5);
    }
    cookieString.push(`Expires=${expiresString};`);
  }
  return cookieString.join(' ');
};

class CookieHelper {
  getCookie = (name: string): string | null => {
    try {
      if (isClientSide) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts?.length === 2) {
          return decodeURIComponent(parts.pop().split(';').shift());
        }
        return null;
      }
      logger.log('CookieHelper.getCookie is not available on server side');
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  setCookie = (name: string, value: string, days: number): void => {
    try {
      if (isClientSide) {
        let expires = '';
        if (days) {
          const date = new Date();
          date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
          expires = `; expires=${date.toUTCString()}`;
        }
        document.cookie = `${name}=${
          encodeURIComponent(value) ?? ''
        }${expires}; path=/`;
      }
      logger.log('CookieHelper.setCookie is not available on server side');
    } catch (error) {
      logger.error(error);
    }
  };

  eraseCookie = (name: string): void => {
    try {
      if (isClientSide) {
        document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      }
      logger.log('CookieHelper.eraseCookie is not available on server side');
    } catch (error) {
      logger.error(error);
    }
  };
}

export const cookieHelper = new CookieHelper();
